import { Capacitor } from '@capacitor/core';
import { QRScannerNative } from './qr-scanner-native';
import {
  BarcodeScanner,
  TorchStateResult,
} from '@capacitor-community/barcode-scanner';

import { pl } from 'date-fns/locale';
// import { QRScanner as QRScannerHtml5 } from './qr-scanner-html5';
// import { QRScanner } from './qr-scanner-wrapperx';

// Hybrid platform detection: Use Capacitor for native mobile, PWA Context for PWA/desktop
interface PlatformDetectionOptions {
  isMobilePWA?: boolean;
  isDesktopPWA?: boolean;
  deviceInfo?: any;
}

// Check if we should use browser camera API (PWA or desktop with camera)
const shouldUseBrowserCamera = (options?: PlatformDetectionOptions) => {
  // Use Capacitor detection for native mobile apps
  const capacitorPlatform = Capacitor.getPlatform();
  const isNativeMobile =
    capacitorPlatform === 'ios' || capacitorPlatform === 'android';

  if (isNativeMobile) return false; // Use native Capacitor scanner

  // For PWA/desktop, use PWA Context detection if available
  if (options?.isMobilePWA || options?.isDesktopPWA) {
    return true; // Use browser camera for PWA
  }

  // Fallback: Check if camera is available (basic check)
  const hasCamera =
    'mediaDevices' in navigator && 'getUserMedia' in navigator.mediaDevices;

  return hasCamera;
  // if (!hasCamera) return false;

  // // Check if running in standalone mode (PWA)
  // const isStandalone =
  //   window.matchMedia('(display-mode: standalone)').matches ||
  //   (window.navigator as any).standalone === true;
  //
  // // Check if on mobile device
  // const isMobileDevice =
  //   /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
  //     navigator.userAgent,
  //   );
  //
  // // Use browser camera for PWA on mobile OR desktop with camera
  // return (isStandalone && isMobileDevice) || (!isMobileDevice && isStandalone);
};

export class QRScannerWrapper {
  private scanner: any; // QRScannerNative | QRScannerHtml5 | null = null;

  constructor(
    private options: any,
    private platformOptions?: PlatformDetectionOptions,
  ) {}

  async start(): Promise<void> {
    try {
      // Use Capacitor plugin for native mobile apps
      if (!shouldUseBrowserCamera(this.platformOptions)) {
        console.log('Using Capacitor scanner for native mobile app');
        // Import and use Capacitor scanner
        await BarcodeScanner.hideBackground();
        return;
      } else if (this.platformOptions?.isDesktopPWA) {
        const { QRScanner: QRScannerHtml5 } = await import(
          './qr-scanner-html5'
        );
        this.scanner = new QRScannerHtml5(this.options);
        await this.scanner.start();
        console.log('Using html5-qrcode scanner');
      } else {
        console.log('Using native scanner for PWA/desktop');
        this.scanner = new QRScannerNative(this.options);
        await this.scanner.start();
      }
    } catch (error) {
      console.error('Failed to start scanner:', error);
      throw error;
    }
  }

  async stop(): Promise<void> {
    if (this.scanner) {
      await this.scanner.stop();
    } else {
      // Stop Capacitor scanner
      await BarcodeScanner.showBackground();
      await BarcodeScanner.stopScan();
      await BarcodeScanner.disableTorch();
    }
  }

  async close(): Promise<void> {
    if (this.scanner) {
      await this.scanner.close();
    }
  }
}

export const startQrScan = (
  platformOptions?: PlatformDetectionOptions,
): Promise<string> => {
  return new Promise(async (resolve, reject) => {
    try {
      // Use Capacitor plugin for native mobile apps
      if (!shouldUseBrowserCamera(platformOptions)) {
        console.log('Using Capacitor scanner for native mobile app');

        await BarcodeScanner.hideBackground();

        const result = await BarcodeScanner.startScan({
          targetedFormats: [
            'QR_CODE',
            'CODE_128',
            'CODE_39',
            'EAN_13',
            'EAN_8',
            'UPC_A',
            'UPC_E',
          ],
          // Specify back camera for better barcode scanning experience
          cameraDirection: 'back',
        });

        if (result.hasContent) {
          await BarcodeScanner.stopScan();
          await BarcodeScanner.disableTorch();
          resolve(result.content);
        } else {
          reject(new Error('No QR code detected'));
        }
        return;
      } else if (
        platformOptions?.isDesktopPWA ||
        (platformOptions?.deviceInfo && !platformOptions.deviceInfo.isPWA)
      ) {
        console.log('DESKTOP PWA', JSON.stringify(platformOptions));
        const { QRScanner: QRScannerHtml5 } = await import(
          './qr-scanner-html5'
        );
        const scanner = new QRScannerHtml5({
          onResult: (result: string) => {
            resolve(result);
          },
          onError: (error: string) => {
            reject(new Error(error));
          },
          onClose: () => {
            reject(new Error('Scanner closed'));
          },
        });
        await scanner.start();
      } else {
        console.log('NOT DESKTOP PWA', JSON.stringify(platformOptions));
        // Use browser camera for PWA/desktop
        console.log('Using native scanner for PWA/desktop');
        const scanner = new QRScannerWrapper(
          {
            onResult: (result: string) => {
              console.log('QR Code detected:', result);
              resolve(result);
            },
            onError: (error: string) => {
              console.error('QR Scanner error:', error);
              reject(new Error(error));
            },
            onClose: () => {
              console.log('QR Scanner closed');
            },
          },
          platformOptions,
        );
        await scanner.start();
      }
    } catch (error) {
      console.error('Failed to start scanner:', error);
      reject(error);
    }
  });
};

// Re-added exports for compatibility with installer-tools.tsx
export const stopQrScan = async (
  platformOptions?: PlatformDetectionOptions,
): Promise<void> => {
  try {
    // Use Capacitor plugin for native mobile apps
    if (!shouldUseBrowserCamera(platformOptions)) {
      await BarcodeScanner.showBackground();
      await BarcodeScanner.stopScan();
      await BarcodeScanner.disableTorch();
    } else {
      console.log('stopQrScan called for browser camera');
    }
  } catch (error) {
    console.error('Error stopping QR scan:', error);
  }
};

export const checkPermissionStatus = async (
  platformOptions?: PlatformDetectionOptions,
): Promise<boolean> => {
  try {
    // Use Capacitor plugin for native mobile apps
    if (!shouldUseBrowserCamera(platformOptions)) {
      await BarcodeScanner.checkPermission({ force: true });
      return true;
    } else {
      // Check browser camera permission
      const result = await navigator.permissions.query({
        name: 'camera' as PermissionName,
      });
      return result.state === 'granted';
    }
  } catch (error) {
    console.error('Error checking camera permission:', error);
    return false;
  }
};

export const toggleTorch = async () => {
  /* TODO:  torch seems to work, but behavior is not correct */
  try {
    const torchState: TorchStateResult = await BarcodeScanner.getTorchState();
    // console.log('[toggle-torch] torch-state', torchState);
    if (!torchState.isEnabled) {
      // console.log('[torch-state]', torchState)
      await BarcodeScanner.enableTorch();
    }
    const isOn = await BarcodeScanner.toggleTorch();
    console.log('[toggle-torch] torch-on', isOn);
  } catch (e) {
    if (e instanceof Error) {
      console.log(`[toggle-torch] error: ${e.message}`);
    } else {
      console.log('[toggle-torch] error: ', e);
    }
  }
};
